import type { FeedLastLocation } from "@/types/user/userInfo";

/** Analysis or Discovery surface key paired with a feed id in cached locations. */
export type FeedSurface = FeedLastLocation["surface"];

const FEED_LAST_LOCATION_PATH = /^\/(analysis|discovery)\/([^/?#]+)/;

/**
 * Parses feed id and surface from an Analysis or Discovery pathname.
 * @param {string} pathname - Route pathname without origin or search.
 * @returns Context for persist/lookup, or `null` on non-feed routes.
 * @example "/analysis/456" → `{ feedId: "456", surface: "ANALYSIS" }`
 */
export const parseFeedLastLocationContext = (
  pathname: string,
): { feedId: string; surface: FeedSurface } | null => {
  const match = FEED_LAST_LOCATION_PATH.exec(pathname);
  if (!match) return null;

  const [, segment, feedId] = match;
  if (!segment || !feedId) return null;

  return {
    feedId,
    surface: segment === "analysis" ? "ANALYSIS" : "DISCOVERY",
  };
};

/**
 * Whether the current pathname should trigger a per-feed location write.
 * @param {string} pathname - Route pathname without origin or search.
 */
export const shouldPersistFeedLastLocation = (pathname: string): boolean =>
  parseFeedLastLocationContext(pathname) !== null;

/**
 * Looks up a cached pathname + search for a feed and surface.
 * @param {FeedLastLocation[] | undefined} locations - Hydrated `me.feedLastLocations`.
 * @param {string} feedId - Target feed id.
 * @param {FeedSurface} surface - Analysis or Discovery surface.
 * @returns Stored location string, or `undefined` when no entry exists.
 */
export const findFeedLastLocation = (
  locations: FeedLastLocation[] | undefined,
  feedId: string,
  surface: FeedSurface,
): string | undefined =>
  locations?.find(
    (entry) => entry.feed_id === feedId && entry.surface === surface,
  )?.location;

/**
 * Validates that a cached location still matches the requested feed and surface.
 * @param {string} location - Pathname + search (no origin).
 * @param {string} feedId - Expected feed id.
 * @param {FeedSurface} surface - Expected surface.
 */
export const isValidCachedFeedLocation = (
  location: string,
  feedId: string,
  surface: FeedSurface,
): boolean => {
  const pathname = location.split("?")[0]?.split("#")[0] ?? location;
  const parsed = parseFeedLastLocationContext(pathname);
  return parsed?.feedId === feedId && parsed?.surface === surface;
};

/**
 * Default feed URL when no valid cached location exists for the surface.
 * @param {string} feedId - Target feed id.
 * @param {FeedSurface} surface - Analysis or Discovery surface.
 * @returns Bare analysis route or Discovery clusters tab.
 */
export const defaultFeedLocation = (
  feedId: string,
  surface: FeedSurface,
): string =>
  surface === "ANALYSIS"
    ? `/analysis/${feedId}`
    : `/discovery/${feedId}/clusters`;

interface FeedRouteTargetCandidate {
  id: string | number;
}

/**
 * Picks the requested feed when it is currently available, otherwise the first available feed.
 * @param {string} feedId - Feed id from the current route.
 * @param {FeedRouteTargetCandidate[]} feeds - Feeds authorized for the current user.
 * @returns Feed id safe to use in feed-scoped routes, or `null` when none exist.
 */
export const resolveAvailableFeedId = (
  feedId: string,
  feeds: FeedRouteTargetCandidate[],
): string | null => {
  const requestedFeed = feeds.find((feed) => String(feed.id) === feedId);
  return requestedFeed != null ? feedId : String(feeds[0]?.id ?? "") || null;
};

/**
 * Resolves the href for feed switcher navigation.
 * @param {string} feedId - Feed being switched to.
 * @param {FeedSurface} surface - Current app surface (Analysis or Discovery).
 * @param {FeedLastLocation[] | undefined} locations - Hydrated per-feed cache from auth state.
 * @returns Cached pathname + search, or {@link defaultFeedLocation} when missing or invalid.
 * @example "/analysis/456?savedViewId=abc" or "/analysis/456"
 */
export const resolveFeedSwitchHref = (
  feedId: string,
  surface: FeedSurface,
  locations: FeedLastLocation[] | undefined,
): string => {
  const cached = findFeedLastLocation(locations, feedId, surface);
  if (cached && isValidCachedFeedLocation(cached, feedId, surface)) {
    return cached;
  }
  return defaultFeedLocation(feedId, surface);
};
